const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/PDFViewer/usePdfPageSizes.ts';
import { useCallback, useRef, useState } from 'react';
import { KeyValuePair } from '../../defs/generalDefs';

const FALLBACK_HEIGHT_AT_100 = 1000;
const FALLBACK_WIDTH_AT_100 = 565;

export type PageSize = { height: number; width: number };

/**
 * Tracks each PDF page's size so a slot can reserve the right space before its <Page> renders
 * (preventing layout shifts on mount). Seeds a document-wide estimate from page 1 — metadata only;
 * we never size every page up front, which would flood the pdf.js worker / exhaust WebView memory
 * on a large PDF — and refines each page's real height as it renders. All sizes are stored at 100%
 * zoom; placeholderSize() re-applies the live scale.
 */
export function usePdfPageSizes() {
    const heights = useRef<Map<number, number>>(new Map());
    const widths = useRef<Map<number, number>>(new Map());
    const estimateHeight = useRef(FALLBACK_HEIGHT_AT_100);
    const estimateWidth = useRef(FALLBACK_WIDTH_AT_100);
    const [, bumpVersion] = useState(0);

    // Read page 1's size at 100% zoom and seed the estimate every unsized slot falls back to.
    const seedFromDocument = useCallback(async (pdf: KeyValuePair) => {
        try {
            const viewport = (await pdf.getPage(1)).getViewport({ scale: 1 });
            heights.current.set(1, viewport.height);
            widths.current.set(1, viewport.width);
            estimateHeight.current = viewport.height;
            estimateWidth.current = viewport.width;
            bumpVersion(v => v + 1);
        } catch (err) {
            console.error('Failed to read PDF page size', err);
        }
    }, []);

    const recordRenderedHeight = useCallback((page: number, offsetHeight: number, scalePercent: number) => {
        const normalized = offsetHeight / (scalePercent / 100);
        heights.current.set(page, normalized);
    }, []);

    // Reserved slot size for a page at the given zoom (used for min-height / min-width).
    const placeholderSize = useCallback(
        (page: number, scalePercent: number): PageSize => ({
            height: (heights.current.get(page) ?? estimateHeight.current) * (scalePercent / 100),
            width: (widths.current.get(page) ?? estimateWidth.current) * (scalePercent / 100),
        }),
        []
    );

    return { seedFromDocument, recordRenderedHeight, placeholderSize };
}
